import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { FilePdf, Play, XCircle } from '@phosphor-icons/react'
import { Banner, Button, useToast } from '@/ui'
import { formatGrams } from '@/lib/weight'
import {
  useAllSkus,
  useCancelPackRun,
  useLabelVersion,
  usePackLotSummary,
  usePackRun,
  type PackLotSummary,
  type PackRunDetail,
  type RunBalance,
} from '../../api/packFull'
import { ActionButton } from '../../components/Can'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { PhotoViewer } from '../../components/PhotoViewer'
import {
  Check,
  Code,
  Facts,
  Kg,
  RefLink,
  Section,
  StatTile,
  Status,
  UserName,
  When,
} from '../../components/bits'
import { RefusalBanner } from '../../components/pack/Refusal'
import { refusalOf } from '../../model/pack/refusal'
import { StartRunDialog } from '../../components/pack/StartRunDialog'
import { openFile } from '../../lib/download'
import { packLotStatusText, runState } from '../../model/pack/packWords'

/** One pack run (F7.1–F7.4): the plan, the batch, the pack lot, balance and label count. */
export function PackRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = usePackRun(id)
  const r = q.data
  const state = r ? runState(r.status, r.startedAt) : null
  return (
    <ConsolePage
      back={{ to: '/c/pack-runs', label: 'Pack runs' }}
      eyebrow="Pack run"
      title={r ? <Code className="text-[inherit]">{r.code}</Code> : 'Pack run'}
      description={
        r ? [r.batchCode, r.locationCode, r.equipmentCode].filter(Boolean).join(' · ') : undefined
      }
      actions={state ? <Status status={state.status} label={state.label} /> : undefined}
    >
      <QueryState query={q} label="this pack run">
        {r && <RunBody r={r} />}
      </QueryState>
    </ConsolePage>
  )
}

function RunBody({ r }: { r: PackRunDetail }) {
  const toast = useToast()
  const skus = useAllSkus()
  const sku = skus.data?.find((s) => s.id === r.skuId) ?? null
  const lotQ = usePackLotSummary(r.packLotId)
  const lot = lotQ.data ?? null
  const label = useLabelVersion(r.labelVersionId)
  const [starting, setStarting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [pdf, setPdf] = useState<string | null>(null)
  const planned = r.status === 'OPEN' && !r.startedAt

  const open = async (kind: 'coder-sheet' | 'carton-labels') => {
    if (!r.packLotId) return
    setPdf(kind)
    try {
      await openFile(
        `/pack-lots/${r.packLotId}/${kind}.pdf`,
        `${kind}-${r.packLotCode ?? r.code}.pdf`,
      )
    } catch (e) {
      const why = refusalOf(e)
      toast.error(
        kind === 'coder-sheet' ? 'Coder sheet not opened' : 'Carton labels not opened',
        why.title,
      )
    } finally {
      setPdf(null)
    }
  }

  // an unstarted run has an all-zero balance row: nothing to judge yet
  const b = r.startedAt ? r.balance : null
  return (
    <>
      {planned && (
        <Banner tone="info" title="Planned — not started yet">
          The line starts it on the pack-hall phone after the cleanout, scale check and label roll
          check. Ask the store keeper to issue the batch's sacks to this run first.
        </Banner>
      )}

      <div className="flex flex-wrap gap-2">
        {planned && (
          <>
            <ActionButton
              perm="pack-runs:start"
              hide
              variant="secondary"
              leadingIcon={<Play />}
              onClick={() => setStarting(true)}
            >
              Start at the hall desk
            </ActionButton>
            <ActionButton
              perm="pack-runs:create"
              hide
              variant="ghost"
              leadingIcon={<XCircle />}
              onClick={() => setCancelling(true)}
            >
              Cancel run
            </ActionButton>
          </>
        )}
        {r.packLotId && (
          <>
            <Button
              variant="secondary"
              leadingIcon={<FilePdf />}
              loading={pdf === 'coder-sheet'}
              onClick={() => void open('coder-sheet')}
            >
              Coder sheet
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<FilePdf />}
              loading={pdf === 'carton-labels'}
              onClick={() => void open('carton-labels')}
            >
              Carton labels
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Planned"
          value={r.plannedG !== null ? formatGrams(r.plannedG) : '—'}
          hint={
            r.plannedPouches !== null
              ? `${r.plannedPouches.toLocaleString('en-IN')} pouches`
              : undefined
          }
        />
        <StatTile
          label="Pouches packed"
          value={lot?.pouchCount ?? '—'}
          hint={
            lot?.packedG !== null && lot?.packedG !== undefined
              ? formatGrams(lot.packedG)
              : r.startedAt
                ? 'Counted at the close'
                : 'Not started'
          }
        />
        <StatTile
          label="Not accounted for"
          value={
            b?.residualPct !== null && b?.residualPct !== undefined ? `${b.residualPct}%` : '—'
          }
          tone={b?.withinLimit === false ? 'danger' : b?.withinLimit ? 'ok' : 'neutral'}
          hint={b?.lossLimitPct ? `Limit ${b.lossLimitPct}%` : 'Worked out at the close'}
        />
        <StatTile
          label="Label count gap"
          value={lot?.labelGap ?? '—'}
          tone={lot?.labelGap ? 'danger' : lot?.labelGap === 0 ? 'ok' : 'neutral'}
          hint={
            lot?.labelsPrinted !== null && lot?.labelsPrinted !== undefined
              ? `${lot.labelsPrinted} codes printed`
              : 'Counted at the close'
          }
        />
      </div>

      <DetailColumns
        main={
          <>
            <Section title="Plan">
              <Facts
                items={[
                  { label: 'Product', value: sku ? `${sku.name} (${sku.code})` : '—' },
                  {
                    label: 'Batch',
                    value: r.batchUnitId ? (
                      <RefLink to={`/c/batches/${r.batchUnitId}`}>{r.batchCode ?? 'Batch'}</RefLink>
                    ) : (
                      '—'
                    ),
                  },
                  {
                    label: 'Planned day',
                    value: r.plannedDate ? <When at={r.plannedDate} dateOnly /> : '—',
                  },
                  { label: 'Kilos', value: <Kg grams={r.plannedG} /> },
                  {
                    label: 'Pouches',
                    value: r.plannedPouches?.toLocaleString('en-IN') ?? '—',
                    mono: true,
                  },
                  { label: 'Pack size', value: sku ? `${sku.netG} g` : '—', mono: true },
                  { label: 'Packing hall', value: r.locationCode ?? '—', mono: true },
                  { label: 'Line', value: r.equipmentCode ?? '—', mono: true },
                  { label: 'Operator', value: r.operatorName ?? <UserName id={r.operatorId} /> },
                  { label: 'Planned on', value: <When at={r.createdAt} /> },
                ]}
              />
            </Section>
            <BalanceSection b={b} r={r} />
            <ReconciliationSection
              lot={lot}
              loading={lotQ.isLoading}
              started={Boolean(r.startedAt)}
            />
            {lot && lot.pouchChecks.length > 0 && <PouchChecks lot={lot} />}
          </>
        }
        side={
          <>
            <Section title="Pack lot">
              {r.packLotId ? (
                <Facts
                  columns={2}
                  items={[
                    {
                      label: 'Code',
                      value: (
                        <RefLink to={`/c/pack-lots/${r.packLotId}`}>
                          {r.packLotCode ?? 'Pack lot'}
                        </RefLink>
                      ),
                    },
                    {
                      label: 'Status',
                      value: r.packLotStatus ? (
                        <Status
                          status={r.packLotStatus}
                          label={packLotStatusText(r.packLotStatus)}
                        />
                      ) : (
                        '—'
                      ),
                    },
                    {
                      label: 'Hold',
                      value:
                        r.packLotHold && r.packLotHold !== 'NONE' ? (
                          <Status status={r.packLotHold} />
                        ) : (
                          'None'
                        ),
                    },
                    {
                      label: 'Pack date',
                      value: lot?.packDate ? <When at={lot.packDate} dateOnly /> : '—',
                    },
                    {
                      label: 'Best before',
                      value: lot?.bestBefore ? <When at={lot.bestBefore} dateOnly /> : '—',
                    },
                    {
                      label: 'Label version',
                      value: lot?.labelCode ?? label.data?.code ?? '—',
                      mono: true,
                    },
                  ]}
                />
              ) : (
                <p className="text-[14px] text-muted">
                  The pack lot, its code, dates and QR link are made when the run starts.
                </p>
              )}
              {lot?.qrUrl && (
                <div className="mt-3">
                  <div className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                    QR link
                  </div>
                  <Code className="break-all">{lot.qrUrl}</Code>
                </div>
              )}
              {lotQ.isError && (
                <p className="mt-2 text-[13px] text-warn">{refusalOf(lotQ.error).title}</p>
              )}
            </Section>
            {r.startedAt && (
              <Section title="Start">
                <Facts
                  columns={2}
                  items={[
                    { label: 'Started', value: <When at={r.startedAt} /> },
                    { label: 'Closed', value: <When at={r.endedAt} /> },
                    { label: 'Cleaned by', value: <UserName id={r.cleanoutById} /> },
                    {
                      label: 'Allergen clean',
                      value: r.allergenCleanRequired
                        ? r.allergenCleanConfirmed
                          ? 'Done (needed)'
                          : 'Needed, not done'
                        : r.allergenCleanConfirmed
                          ? 'Done'
                          : 'Not needed',
                    },
                    { label: 'Left from last run', value: <Kg grams={r.leftoverG} /> },
                    {
                      label: 'Scale check',
                      value: r.scaleCheck
                        ? `${r.scaleCheck.readingG ?? '—'} g for ${r.scaleCheck.referenceG ?? '—'} g`
                        : '—',
                      mono: true,
                    },
                  ]}
                />
                {r.cleanoutPhotoId && (
                  <div className="mt-3">
                    <div className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                      Empty hopper
                    </div>
                    <PhotoViewer
                      photo={r.cleanoutPhotoId}
                      alt="Empty hopper at cleanout"
                      size="sm"
                    />
                  </div>
                )}
              </Section>
            )}
            {label.data && (
              <Section title="Label lines">
                <Facts
                  columns={2}
                  items={[
                    { label: 'Version', value: label.data.code, mono: true },
                    {
                      label: 'MRP',
                      value: label.data.mrpInr ? `₹${label.data.mrpInr}` : '—',
                      mono: true,
                    },
                    { label: 'Allergen line', value: label.data.allergenStatement ?? '—' },
                    { label: 'Claim', value: label.data.claimText ?? '—' },
                  ]}
                />
              </Section>
            )}
          </>
        }
      />

      <StartRunDialog
        run={r}
        netG={sku?.netG ?? null}
        open={starting}
        onClose={() => setStarting(false)}
      />
      <CancelRun r={r} open={cancelling} onClose={() => setCancelling(false)} />
    </>
  )
}

function BalanceSection({ b, r }: { b: RunBalance | null; r: PackRunDetail }) {
  if (!b) {
    return (
      <Section title="Balance">
        <p className="text-[14px] text-muted">
          {r.startedAt ? 'Worked out when the run closes.' : 'Not started yet.'}
        </p>
      </Section>
    )
  }
  return (
    <Section
      title="Balance"
      actions={
        b.withinLimit === null ? undefined : (
          <Check
            ok={b.withinLimit}
            label={b.withinLimit ? 'Within the loss limit' : 'Over the loss limit'}
          />
        )
      }
    >
      <Facts
        columns={4}
        items={[
          { label: 'Kilos in', value: <Kg grams={b.inputG} /> },
          { label: 'Packed', value: <Kg grams={b.packedG} /> },
          { label: 'Other outputs', value: <Kg grams={b.otherG} /> },
          {
            label: 'Not accounted for',
            value: (
              <span className={b.withinLimit === false ? 'text-danger' : undefined}>
                <Kg grams={b.residualG} />{' '}
                {b.residualPct !== null && <span className="text-muted">({b.residualPct}%)</span>}
              </span>
            ),
          },
        ]}
      />
      <p className="mt-3 text-[13px] text-muted">
        {b.lossLimitPct
          ? `Up to ${b.lossLimitPct}% may be lost or gained in packing.`
          : 'No loss limit set for packing.'}{' '}
        Other outputs are leftover sacks, spillage and rejected pouches.
        {b.checkedAt && (
          <>
            {' '}
            Checked <When at={b.checkedAt} />.
          </>
        )}
      </p>
    </Section>
  )
}

function ReconciliationSection({
  lot,
  loading,
  started,
}: {
  lot: PackLotSummary | null
  loading: boolean
  started: boolean
}) {
  if (!started) return null
  if (loading)
    return (
      <Section title="Label count">
        <p className="text-[14px] text-muted">Loading…</p>
      </Section>
    )
  if (!lot || lot.labelsPrinted === null) {
    return (
      <Section title="Label count">
        <p className="text-[14px] text-muted">
          The operator counts printed codes when closing the run.
        </p>
      </Section>
    )
  }
  const gap = lot.labelGap ?? 0
  return (
    <Section
      title="Label count"
      actions={
        <Check
          ok={gap === 0}
          label={gap === 0 ? 'Adds up' : `${Math.abs(gap)} ${gap > 0 ? 'unaccounted' : 'too many'}`}
        />
      }
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 font-mono text-[15px] tabular">
        <span>
          <b>{lot.labelsPrinted}</b>{' '}
          <span className="font-sans text-[13px] text-muted">printed</span>
        </span>
        <span>=</span>
        <span>
          <b>{lot.labelsApplied ?? 0}</b>{' '}
          <span className="font-sans text-[13px] text-muted">on good pouches</span>
        </span>
        <span>+</span>
        <span>
          <b>{lot.labelsSpoiled ?? 0}</b>{' '}
          <span className="font-sans text-[13px] text-muted">spoiled</span>
        </span>
        <span>+</span>
        <span>
          <b>{lot.labelsDestroyed ?? 0}</b>{' '}
          <span className="font-sans text-[13px] text-muted">destroyed</span>
        </span>
        {gap !== 0 && (
          <span className="text-danger">
            {gap > 0 ? '+' : '−'} <b>{Math.abs(gap)}</b>{' '}
            <span className="font-sans text-[13px]">
              {gap > 0 ? 'not accounted for' : 'more than printed'}
            </span>
          </span>
        )}
      </div>
      {gap !== 0 && (
        <p className="mt-2 text-[13px] text-danger">
          The lot cannot be released until the quality person has looked at this.
        </p>
      )}
      {lot.counterPhotoId && (
        <div className="mt-3">
          <div className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
            Coder counter
          </div>
          <PhotoViewer photo={lot.counterPhotoId} alt="Coder print counter" size="sm" />
        </div>
      )}
    </Section>
  )
}

function PouchChecks({ lot }: { lot: PackLotSummary }) {
  const POS: Record<string, string> = {
    FIRST: 'First pouch',
    LAST: 'Last pouch',
    MIDDLE: 'Spot check',
  }
  return (
    <Section title="Pouch checks" bodyClassName="p-0">
      <ul className="divide-y divide-rule/70">
        {lot.pouchChecks.map((c, i) => (
          <li key={i} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-[14px]">
            <PhotoViewer
              photo={c.photoId}
              alt={`${POS[c.position ?? ''] ?? 'Pouch'} scan`}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{POS[c.position ?? ''] ?? 'Pouch'}</span>
                <Check ok={c.match} label={c.match ? 'Code matches' : 'Code does not match'} />
              </div>
              {c.scannedText && <Code className="block break-all text-muted">{c.scannedText}</Code>}
            </div>
            <span className="text-[12.5px] text-muted">
              <When at={c.at} />
            </span>
          </li>
        ))}
      </ul>
    </Section>
  )
}

function CancelRun({ r, open, onClose }: { r: PackRunDetail; open: boolean; onClose: () => void }) {
  const cancel = useCancelPackRun()
  const close = () => {
    cancel.reset()
    onClose()
  }
  return (
    <ConfirmDialog
      open={open}
      onClose={close}
      title={`Cancel ${r.code}?`}
      description="Only a run that has not started can be cancelled. Its kilos go back to the batch."
      confirmLabel="Cancel run"
      tone="danger"
      loading={cancel.isPending}
      reason={{
        label: 'Why',
        placeholder: 'e.g. Wrong product picked',
        minLength: 3,
        multiline: false,
      }}
      onConfirm={async (reason) => {
        await cancel.mutateAsync({ id: r.id, reason })
        close()
      }}
    >
      {cancel.isError && <RefusalBanner error={cancel.error} />}
    </ConfirmDialog>
  )
}
